import React from 'react';
import { selectShowParticipantNamesInSystemSession } from 'features/layout/features/config/systemSessionSelectors';
import { AvatarSize } from 'features/layout/features/dimensions/types';
import { getInitials } from 'utils/getInitials';
import { useAppSelector } from 'store/hooks';
import { selectUserById } from 'features/users/usersSlice';
import { SourceDetails, SourceKind } from 'features/layout/types';
import { StreamBadge } from 'features/streaming/components/stream-display/StreamBadge';
import { UserStreamBadge } from 'features/streaming/components/stream-display/UserStreamBadge';
import { useTranslation } from 'react-i18next';
import Avatar from 'features/streaming/components/stream-display/Avatar';
import { SystemSessionGuard } from 'components/SystemSessionGuard';
import { UserName } from 'features/users/types';
import { useSystemSession } from 'hooks/useSystemSession';

type StreamMediaProps = {
  source: SourceDetails;
  children: React.ReactNode;
  avatarSize?: AvatarSize;
  hideAvatar?: boolean;
};

const StreamMedia = React.memo(({ source, children, avatarSize, hideAvatar }: StreamMediaProps) => {
  const { t } = useTranslation();
  const showBadge = useAppSelector(selectShowParticipantNamesInSystemSession);
  const user = useAppSelector((state) => selectUserById(state, source.userId));

  const isSystemSession = useSystemSession();

  if (!user) {
    return null;
  }

  const labels: Record<SourceKind, UserName> = {
    'screenshare-local': `${t('screenshare.single')}`,
    'screenshare-remote': `${user.name} (${t('screenshare.single')})`,
    local: t('you'),
    debug: user.name,
    remote: user.name,
    external: t('stream'),
    custom: null,
  };

  const badgeLabel = labels[source.kind];
  const isUserTile = source.kind === 'local' || source.kind === 'remote';
  const badgeClassName = isSystemSession ? 'hover-child-eager' : 'hover-child';

  return (
    <>
      {source.kind !== 'screenshare-local' &&
        source.kind !== 'screenshare-remote' &&
        source.kind !== 'external' && (
          <SystemSessionGuard show={showBadge}>
            {isUserTile ? (
              <UserStreamBadge label={badgeLabel} user={user} className={badgeClassName} />
            ) : (
              <StreamBadge label={badgeLabel} className={badgeClassName} />
            )}
          </SystemSessionGuard>
        )}
      {!hideAvatar && (
        <Avatar
          src={user.avatarUrl}
          size={avatarSize}
          sx={{
            bgcolor: user.avatarColor,
          }}
        >
          {user.initials || getInitials(user.name)}
        </Avatar>
      )}
      {children}
    </>
  );
});

export default StreamMedia;
